import { useEffect, useState } from 'react';

/** The line Tailwind's `md` breakpoint draws, shared so nobody writes it twice. */
export const PHONE_QUERY = '(max-width: 767px)';

/**
 * Whether this is a phone-sized window — the same line Tailwind's `md`
 * breakpoint draws, so a component can switch what it *renders* below it
 * (a row list instead of a table) while the stylesheet switches how things
 * *look*. The two must agree or a phone would get a table styled for a phone
 * and a laptop a list styled for a laptop.
 */
export function useIsPhone(): boolean {
  return useMedia(PHONE_QUERY);
}

/** Whether media queries can be asked here at all (a server render, or a test runner, cannot). */
const canAsk = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/** Whether a media query matches now, kept current as the window changes. */
export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => canAsk() && window.matchMedia(query).matches);
  useEffect(() => {
    if (!canAsk()) return;
    const mql = window.matchMedia(query);
    const apply = () => setMatches(mql.matches);
    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [query]);
  return matches;
}
